import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Search as SearchIcon, 
  FileUpload as FileUploadIcon,
  Notifications as NotificationsIcon 
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Location } from '../../types/Location';
import LocationImport from './LocationImport';

interface LocationWithFavorite extends Location {
  isFavorite?: boolean;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

interface OrderingInfo {
  orderBy: string;
  orderDirection: 'ASC' | 'DESC';
}

interface PaginatedResponse {
  data: LocationWithFavorite[];
  pagination: PaginationInfo;
  ordering: OrderingInfo;
}

type OrderByField = 'id' | 'name' | 'address' | 'createdAt' | 'updatedAt';
type OrderDirection = 'ASC' | 'DESC';

const LocationsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, token } = useAuth();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<LocationWithFavorite | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<OrderByField>('name');
  const [orderDirection, setOrderDirection] = useState<OrderDirection>('ASC');
  
  // Push notification state
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationWithFavorite | null>(null);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Debounce the filter to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filter);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filter]);

  // Debug authentication state
  console.log('Auth state:', { isAuthenticated, token: token ? 'exists' : 'missing' });
  console.log('Axios headers:', axios.defaults.headers.common);

  const { data: locationsData, isLoading, error } = useQuery({
    queryKey: ['locations', page, rowsPerPage, debouncedFilter, orderBy, orderDirection],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/locations/paginated`, {
        params: {
          page: page + 1, // API expects 1-based pagination
          limit: rowsPerPage,
          orderBy,
          orderDirection,
          name: debouncedFilter || undefined // Add server-side filtering by name
        }
      });
      console.log('API Response:', response.data); // Debug log
      return response.data as PaginatedResponse;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Extract data from the new API response structure
  const locations = locationsData?.data || [];
  const pagination = locationsData?.pagination || {
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  };

  // Debug logging
  console.log('locationsData:', locationsData);
  console.log('locations array:', locations);
  console.log('pagination:', pagination);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${process.env.REACT_APP_API_URL}/locations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
    },
  });

  const sendPushMutation = useMutation({
    mutationFn: async ({ locationId, title, body }: { locationId: string; title: string; body: string }) => {
      // Send push notification to clients who have favorited this location
      const payload = {
        title,
        body,
        data: {
          locationId,
          type: 'location_update',
          timestamp: new Date().toISOString()
        },
        locationId, // This will be used by the backend to find clients who favorited this location
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/push/notify-location`, payload);
    },
    onSuccess: () => {
      setNotification({
        open: true,
        message: t('locations.pushNotificationSent'),
        severity: 'success',
      });
      setPushDialogOpen(false);
      setSelectedLocation(null);
      setPushTitle('');
      setPushBody('');
    },
    onError: (err) => {
      console.error('Error sending push notification:', err);
      setNotification({
        open: true,
        message: t('locations.pushNotificationFailed'),
        severity: 'error',
      });
    },
  });

  const handleDeleteClick = (location: LocationWithFavorite) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (locationToDelete?.id) {
      deleteMutation.mutate(locationToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLocationToDelete(null);
  };

  const handlePushClick = (location: LocationWithFavorite) => {
    setSelectedLocation(location);
    setPushTitle(`${location.name} - Update`);
    setPushBody('');
    setPushDialogOpen(true);
  };

  const handlePushSend = () => {
    if (selectedLocation?.id && pushTitle.trim() && pushBody.trim()) {
      sendPushMutation.mutate({
        locationId: selectedLocation.id,
        title: pushTitle.trim(),
        body: pushBody.trim(),
      });
    }
  };

  const handlePushCancel = () => {
    setPushDialogOpen(false);
    setSelectedLocation(null);
    setPushTitle('');
    setPushBody('');
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0); // Reset page when filter changes
  };

  const handleOrderByChange = (event: SelectChangeEvent<OrderByField>) => {
    setOrderBy(event.target.value as OrderByField);
    setPage(0); // Reset page when ordering changes
  };

  const handleOrderDirectionChange = (event: SelectChangeEvent<OrderDirection>) => {
    setOrderDirection(event.target.value as OrderDirection);
    setPage(0); // Reset page when ordering changes
  };

  if (isLoading) {
    return <Typography>{t('locations.loading')}</Typography>;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {error?.response?.status === 401 
            ? t('locations.authRequired')
            : t('locations.errorLoading')
          }
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">{t('locations.title')}</Typography>
        {isAuthenticated && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              {t('locations.import')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/locations/new')}
            >
              {t('locations.addLocation')}
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          label={t('locations.searchByName')}
          value={filter}
          onChange={handleFilterChange}
          placeholder={t('locations.searchPlaceholder')}
          variant="outlined"
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('locations.orderBy')}</InputLabel>
          <Select
            value={orderBy}
            label={t('locations.orderBy')}
            onChange={handleOrderByChange}
          >
            <MenuItem value="name">{t('locations.name')}</MenuItem>
            <MenuItem value="address">{t('locations.address')}</MenuItem>
            <MenuItem value="createdAt">{t('locations.createdDate')}</MenuItem>
            <MenuItem value="updatedAt">{t('locations.updatedDate')}</MenuItem>
            <MenuItem value="id">ID</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('locations.direction')}</InputLabel>
          <Select
            value={orderDirection}
            label={t('locations.direction')}
            onChange={handleOrderDirectionChange}
          >
            <MenuItem value="ASC">{t('locations.ascending')}</MenuItem>
            <MenuItem value="DESC">{t('locations.descending')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('locations.name')}</TableCell>
              <TableCell>{t('locations.address')}</TableCell>
              <TableCell>{t('locations.contact')}</TableCell>
              {isAuthenticated && <TableCell>{t('locations.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {locations?.map((location: LocationWithFavorite) => {
              console.log('Location object:', location); // Debug log
              return (
                <TableRow key={location.id || `location-${Math.random()}`}>
                  <TableCell>{location.name}</TableCell>
                  <TableCell>
                    {location.street}<br />
                    {location.zip} {location.city}<br />
                    {location.country}
                  </TableCell>
                  <TableCell>
                    {location.phone && <div key={`${location.id || 'no-id'}-phone`}>📞 {location.phone}</div>}
                    {location.email && <div key={`${location.id || 'no-id'}-email`}>✉️ {location.email}</div>}
                    {location.website && <div key={`${location.id || 'no-id'}-website`}>🌐 {location.website}</div>}
                  </TableCell>
                  {isAuthenticated && (
                    <TableCell>
                      <IconButton
                        onClick={() => handlePushClick(location)}
                        size="small"
                        color="primary"
                        title={t('locations.sendPushNotification')}
                      >
                        <NotificationsIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          if (location.id) {
                            navigate(`/locations/${location.id}/edit`);
                          } else {
                            console.error('Location ID is undefined:', location);
                          }
                        }}
                        size="small"
                        title={t('locations.editLocation')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(location)}
                        size="small"
                        color="error"
                        title={t('locations.deleteLocation')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('locations.confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('locations.deleteConfirmation', { name: locationToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push Notification Dialog */}
      <Dialog
        open={pushDialogOpen}
        onClose={handlePushCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="push-dialog-title"
      >
        <DialogTitle id="push-dialog-title">
          {t('pushNotification.sendPushNotification')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('pushNotification.notificationDescription', { locationName: selectedLocation?.name })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('pushNotification.notificationTitle')}
            fullWidth
            variant="outlined"
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('pushNotification.notificationMessage')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={pushBody}
            onChange={(e) => setPushBody(e.target.value)}
            placeholder={t('pushNotification.notificationMessagePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePushCancel} color="primary">
            {t('pushNotification.cancel')}
          </Button>
          <Button 
            onClick={handlePushSend} 
            color="primary" 
            variant="contained"
            disabled={!pushTitle.trim() || !pushBody.trim() || sendPushMutation.isPending}
            startIcon={<NotificationsIcon />}
          >
            {sendPushMutation.isPending ? t('pushNotification.sending') : t('pushNotification.sendPush')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <LocationImport
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationsList; 